<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { trans, transChoice } from 'laravel-vue-i18n'
import _ from 'lodash'
import PhotoSwipe from 'photoswipe'
import 'photoswipe/style.css'
import RentalCheckoutDrawer from '../wizard/RentalCheckoutDrawer.vue'
import RentalWidgetHeader from './RentalWidgetHeader.vue'
import PoweredByRoori from '../../../administration/components/ui-components/PoweredByRoori.vue'
import { buildCalCells, sameDay, toYMD, getCalCellClass } from '../../composables/useRentalCalendar.js'

const props = defineProps({
    item: Object,
    forms: Object,
    initialDates: Object,
    initialGuests: Object,
    wizardTheme: Object,
    isOverlay: Boolean,
    darkMode: { type: Boolean, default: false },
})

const emit = defineEmits(['close', 'contact'])

// ── Locale ─────────────────────────────────────────────────────────────────
const intlLocale = computed(() => (props.forms.locale ?? 'cs').replace('_', '-'))

const firstDayOfWeek = computed(() => {
    try {
        const info = new Intl.Locale(intlLocale.value).weekInfo
        if (info?.firstDay !== undefined) return info.firstDay
    } catch {}
    const sundayFirst = ['en', 'zh', 'ja', 'ko', 'ar', 'fa', 'he']
    return sundayFirst.includes(intlLocale.value.split('-')[0]) ? 0 : 1
})

const MONTHS = computed(() => {
    const fmt = new Intl.DateTimeFormat(intlLocale.value, { month: 'long' })
    return Array.from({ length: 12 }, (_, i) => {
        const s = fmt.format(new Date(2024, i, 1))
        return s.charAt(0).toUpperCase() + s.slice(1)
    })
})

const DOW = computed(() => {
    const fmt = new Intl.DateTimeFormat(intlLocale.value, { weekday: 'short' })
    return Array.from({ length: 7 }, (_, i) => {
        const dow = (firstDayOfWeek.value + i) % 7
        const s = fmt.format(new Date(2024, 0, 7 + dow))
        return s.charAt(0).toUpperCase() + s.slice(1)
    })
})

function fmtDateShort(d) {
    return d.toLocaleDateString(intlLocale.value, { day: 'numeric', month: 'short' })
}

function fmtKc(amount, currency = 'CZK') {
    return new Intl.NumberFormat(intlLocale.value, { style: 'currency', currency, minimumFractionDigits: 0 }).format(amount)
}

const UNIT_LABELS = {
    hour: trans('rental.unit_abbr_hour'),
    half_day: trans('rental.unit_abbr_half_day'),
    day: trans('rental.unit_abbr_day'),
    night: trans('rental.unit_abbr_night'),
    week: trans('rental.unit_abbr_week'),
    half_month: trans('rental.unit_abbr_half_month'),
    month: trans('rental.unit_abbr_month'),
    quarter: trans('rental.unit_abbr_quarter'),
    year: trans('rental.unit_abbr_year'),
    weekend: trans('rental.unit_abbr_weekend'),
    accommodation: trans('rental.unit_abbr_accommodation'),
}

// ── Item helpers ────────────────────────────────────────────────────────────
const isAccommodation = computed(() => props.forms.form_type?.name === 'accommodation')

const a11yStyle = ref({})

const formInitials = computed(() => {
    const name = props.forms?.name ?? ''
    return name.split(' ').slice(0, 2).map(w => w[0] ?? '').join('').toUpperCase() || 'R'
})
const isSingleDay = computed(() => ['hour', 'half_day'].includes(props.item?.unit))
function itemBasePrice() { return props.item?.prices?.[0]?.unit_price ?? 0 }
function itemUnit() { return UNIT_LABELS[props.item?.unit] ?? (props.item?.unit ?? trans('rental.unit_abbr_night')) }

// ── Blocking days ───────────────────────────────────────────────────────────
const DAY_NAME_TO_DOW = {
    monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0,
}

function blockedDaysOfType(type) {
    if (!props.item) return new Set()
    const blocked = new Set()
    for (let dow = 0; dow <= 6; dow++) {
        if (props.item.booking_rules?.some(r => r.blocking_type === type && DAY_NAME_TO_DOW[r.day] === dow)) {
            blocked.add(dow)
        }
    }
    return blocked
}

const blockedStartDays = computed(() => blockedDaysOfType('start'))
const blockedEndDays = computed(() => blockedDaysOfType('end'))
const blockedIntersectDays = computed(() => blockedDaysOfType('intersect'))

function rangeContainsBlockedIntersect(startDate, endDate) {
    if (!blockedIntersectDays.value.size) return false
    const days = Math.round((endDate - startDate) / 86400000)
    if (days >= 7) return true
    for (let i = 1; i < days; i++) {
        if (blockedIntersectDays.value.has(new Date(startDate.getTime() + i * 86400000).getDay())) return true
    }
    return false
}

// ── Date state ──────────────────────────────────────────────────────────────
const selectedDates = ref(props.initialDates ? { ...props.initialDates } : null)
const calView = ref({ y: new Date().getFullYear(), m: new Date().getMonth() })
const calHovered = ref(null)
const calOpen = ref(false)
const busyDaysData = ref({})

const calCells = computed(() => buildCalCells(calView.value.y, calView.value.m, firstDayOfWeek.value))

const nightsDiff = computed(() => {
    if (!selectedDates.value?.start || !selectedDates.value?.end) return 0
    return Math.round((new Date(selectedDates.value.end) - new Date(selectedDates.value.start)) / 86400000)
})

const minUnits = computed(() => props.item?.min_unit_quantity ?? 1)

function isCalCellTooClose(cell) {
    if (minUnits.value <= 1) return false
    const s = selectedDates.value?.start ? new Date(selectedDates.value.start) : null
    if (!s || selectedDates.value?.end) return false
    if (cell.d <= s) return false
    return Math.round((cell.d - s) / 86400000) < minUnits.value
}

function calCellClass(cell) {
    const cls = getCalCellClass(cell, {
        hoveredDate: calHovered.value,
        selectedDates: selectedDates.value,
        blockedStartDays: blockedStartDays.value,
        blockedEndDays: blockedEndDays.value,
        busyDaysData: busyDaysData.value,
        rangeContainsBlockedIntersect,
    })
    if (isCalCellTooClose(cell)) { cls['blocked-end'] = true; cls['in-range'] = false }
    return cls
}

function calSetHover(cell) {
    if (cell.out) return
    const today = new Date(); today.setHours(0, 0, 0, 0)
    if (cell.d >= today) calHovered.value = cell.d
}

function calShift(n) {
    let m = calView.value.m + n, y = calView.value.y
    while (m < 0) { m += 12; y-- }
    while (m > 11) { m -= 12; y++ }
    calView.value = { y, m }
}

function handleCalPick(cell) {
    if (cell.out) return
    const today = new Date(); today.setHours(0, 0, 0, 0)
    if (cell.d < today) return
    if (isCalCellTooClose(cell)) return
    const d = cell.d
    const s = selectedDates.value?.start ? new Date(selectedDates.value.start) : null
    const e = selectedDates.value?.end ? new Date(selectedDates.value.end) : null
    if (!s || (s && e) || d < s) {
        if (blockedStartDays.value.has(d.getDay())) return
        selectedDates.value = { start: toYMD(d), end: null }
    } else if (d > s && (blockedEndDays.value.has(d.getDay()) || rangeContainsBlockedIntersect(s, d))) {
        return
    } else if (sameDay(d, s)) {
        const tomorrow = new Date(d); tomorrow.setDate(d.getDate() + 1)
        selectedDates.value = { start: toYMD(d), end: toYMD(tomorrow) }
    } else {
        selectedDates.value = { start: selectedDates.value.start, end: toYMD(d) }
        calOpen.value = false
    }
}

// ── Guests ─────────────────────────────────────────────────────────────────
const guests = ref({
    adults: props.initialGuests?.adults ?? 1,
    children: props.initialGuests?.children ?? 0,
    pets: props.initialGuests?.pets ?? 0,
})
const guestsOpen = ref(false)

// ── Busy days ───────────────────────────────────────────────────────────────
async function fetchBusyDays({ y, m }) {
    try {
        const start = `${y}-${String(m + 1).padStart(2, '0')}-01`
        const eY = m >= 11 ? y + 1 : y, eM = m >= 11 ? 0 : m + 1
        const lastDay = new Date(eY, eM + 1, 0).getDate()
        const end = `${eY}-${String(eM + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
        const res = await axios.get(`/api/rental/busy-days/${props.forms.slug}/${start}/${end}`)
        busyDaysData.value = { ...busyDaysData.value, ...res.data }
    } catch {}
}

const fetchBusyDaysDebounced = _.debounce(fetchBusyDays, 300)
watch(calView, (val) => fetchBusyDaysDebounced(val))
onMounted(() => fetchBusyDays(calView.value))

// ── Price breakdown ─────────────────────────────────────────────────────────
const priceBreakdown = computed(() => {
    if (!props.item) return null
    const base = itemBasePrice()
    const nights = nightsDiff.value
    let discountPct = 0, discountAmount = 0
    if (nights > 0) {
        const applicable = [...(props.item.discounts ?? [])]
            .filter(d => d.min_units <= nights).sort((a, b) => b.min_units - a.min_units)[0]
        discountPct = applicable?.percentage_off ?? 0
        discountAmount = base * nights * (discountPct / 100)
    }
    const subtotal = base * nights
    const total = Math.round((subtotal - discountAmount) * 100) / 100
    return { nights, base, subtotal, discountPct, discountAmount, total }
})

// ── Gallery ─────────────────────────────────────────────────────────────────
const galleryImages = computed(() => {
    const imgs = props.item?.gallery ?? []
    return imgs.length ? imgs : Array.from({ length: 5 }, (_, i) => ({ full_url: null, _idx: i }))
})

async function openLightbox(startIndex) {
    const validImages = galleryImages.value.filter(img => img.full_url)
    if (!validImages.length) return

    const slides = await Promise.all(validImages.map(img => new Promise(resolve => {
        const el = new Image()
        el.onload = () => resolve({ src: img.full_url, width: el.naturalWidth, height: el.naturalHeight })
        el.onerror = () => resolve({ src: img.full_url, width: 1200, height: 800 })
        el.src = img.full_url
    })))

    const pswp = new PhotoSwipe({
        dataSource: slides,
        index: Math.min(startIndex, slides.length - 1),
    })
    pswp.init()
}

// ── Attributes ──────────────────────────────────────────────────────────────
const visibleAttributes = computed(() => (props.item?.attributes ?? []).filter(a => a.is_visible).slice(0, 8))

// ── Checkout ────────────────────────────────────────────────────────────────
const checkoutVisible = ref(false)

function openCheckout() {
    checkoutVisible.value = true
}

// ── Keyboard ────────────────────────────────────────────────────────────────
function onKeyDown(e) {
    if (e.key === 'Escape' && props.isOverlay && !checkoutVisible.value) emit('close')
}
onMounted(() => document.addEventListener('keydown', onKeyDown))
onUnmounted(() => document.removeEventListener('keydown', onKeyDown))
</script>

<template>
    <div class="rid-page" :class="{ 'is-transparent': !isOverlay }" :style="a11yStyle">
        <!-- Header -->
        <rental-widget-header
            :forms="forms"
            :sticky="isOverlay"
            :dark-mode="darkMode"
            v-model:a11y-style="a11yStyle"
        >
            <template v-if="isOverlay">
                <button class="rid-close-btn" @click="emit('close')" :aria-label="$t('common.back')">
                    <svg class="rid-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M6 18 18 6"/></svg>
                    <span>{{ $t('common.back') }}</span>
                </button>
            </template>
            <template v-else>
                <button class="rw-hdr-link" @click="emit('contact')">
                    <svg class="rw-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="2" y="4" width="20" height="16" rx="2"/>
                        <path d="m2 7 10 8 10-8"/>
                    </svg>
                    {{ $t('widget.contact') }}
                </button>
                <span class="rw-hdr-divider"/>
                <div class="rw-avatar">{{ formInitials }}</div>
            </template>
        </rental-widget-header>

        <div class="rid-shell z-20 relative">
            <!-- Hero: title + gallery -->
            <section class="rid-hero">
                <div class="rid-hero-head">
                    <div>
                        <div class="rid-eyebrow">
                            <span>{{ isAccommodation ? $t('widget.category_accommodation') : $t('widget.category_rental') }}</span>
                        </div>
                        <h1 class="rid-title">{{ item.name }}</h1>
                        <div v-if="item.attributes?.length" class="rid-sub">
                            <span v-for="attr in item.attributes.filter(a => a.is_visible).slice(0, 3)" :key="attr.id">
                                {{ attr.label }}<template v-if="attr.value"> · {{ attr.value }}</template>
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Gallery grid -->
                <div class="rid-gallery">
                    <div class="rid-gallery-cell rid-gallery-main" @click="openLightbox(0)">
                        <img v-if="galleryImages[0]?.full_url" :src="galleryImages[0].full_url" :alt="item.name"/>
                    </div>
                    <div v-for="n in 4" :key="n" class="rid-gallery-cell" :class="{'rid-gallery-last': n === 4}" @click="openLightbox(n)">
                        <img v-if="galleryImages[n]?.full_url" :src="galleryImages[n].full_url" :alt="`${item.name} ${n+1}`"/>
                        <div v-if="n === 4 && galleryImages.length > 5" class="rid-more-overlay">{{ $t('widget.photos_more', { count: galleryImages.length - 5 }) }}</div>
                    </div>
                </div>
            </section>

            <!-- Main: info + booking widget -->
            <div class="rid-main">
                <!-- Left: info -->
                <div class="rid-info">
                    <!-- Description -->
                    <div v-if="item.description" class="rid-section">
                        <div class="rid-section-hd">
                            <h3>{{ isAccommodation ? $t('widget.about_space') : $t('widget.about_equipment') }}</h3>
                            <span class="rid-bk-tag">{{ $t('widget.tag_description') }}</span>
                        </div>
                        <div class="rid-desc" v-html="item.description"/>
                    </div>

                    <!-- Attributes / amenities -->
                    <div v-if="visibleAttributes.length" class="rid-section">
                        <div class="rid-section-hd">
                            <h3>{{ isAccommodation ? $t('widget.included_accommodation') : $t('widget.included_rental') }}</h3>
                            <span class="rid-bk-tag">{{ $t('widget.tag_amenities') }}</span>
                        </div>
                        <div class="rid-amenities">
                            <div v-for="attr in visibleAttributes" :key="attr.id" class="rid-amenity">
                                <div class="rid-amenity-dot"/>
                                <div>
                                    <b>{{ attr.label }}</b>
                                    <span v-if="attr.value">{{ attr.value }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Rules -->
                    <div class="rid-section">
                        <div class="rid-section-hd">
                            <h3>{{ $t('widget.rules_header') }}</h3>
                            <span class="rid-bk-tag">info</span>
                        </div>
                        <div class="rid-rules">
                            <div v-if="item.min_unit_quantity > 1" class="rid-rule">
                                <span class="rid-rule-lbl">{{ $t('widget.min_duration') }}</span>
                                <span class="rid-rule-val">{{ item.min_unit_quantity }} × {{ UNIT_LABELS[item.unit] ?? item.unit }}</span>
                            </div>
                            <div class="rid-rule">
                                <span class="rid-rule-lbl">{{ $t('widget.payment_label') }}</span>
                                <span class="rid-rule-val">{{ $t('widget.payment_on_confirm') }}</span>
                            </div>
                            <div v-if="item.max_pop" class="rid-rule">
                                <span class="rid-rule-lbl">{{ $t('widget.capacity_label') }}</span>
                                <span class="rid-rule-val">{{ $t('widget.capacity_max_persons', { count: item.max_pop }) }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Right: booking widget -->
                <aside class="rid-booking">
                    <div class="rid-bk-frame">
                        <!-- Price + unit -->
                        <div class="rid-bk-hd">
                            <div class="rid-bk-price">
                                {{ fmtKc(itemBasePrice()) }}<small>/ {{ itemUnit() }}</small>
                            </div>
                        </div>

                        <!-- Single-day item: just show CTA -->
                        <template v-if="isSingleDay">
                            <p class="rid-bk-single-hint">{{ $t('widget.select_slot_hint') }}</p>
                            <button class="rid-btn rid-btn-primary rid-cta" @click="openCheckout">
                                {{ $t('widget.select_date_label') }}
                                <svg class="rid-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                            </button>
                        </template>

                        <!-- Multi-day item: date picker + optional guests + price breakdown -->
                        <template v-else>
                            <!-- Date block -->
                            <div class="rid-bk-block">
                                <div class="rid-bk-block-row">
                                    <button class="rid-bk-cell" :class="{'is-active': calOpen}" @click="calOpen = !calOpen; guestsOpen = false">
                                        <span class="rid-bk-lbl">{{ isAccommodation ? $t('widget.arrival') : $t('widget.date_start') }}</span>
                                        <span class="rid-bk-val" :class="{'rid-muted': !selectedDates?.start}">{{ selectedDates?.start ? fmtDateShort(new Date(selectedDates.start)) : '—' }}</span>
                                    </button>
                                    <div class="rid-bk-sep"/>
                                    <button class="rid-bk-cell" :class="{'is-active': calOpen}" @click="calOpen = !calOpen; guestsOpen = false">
                                        <span class="rid-bk-lbl">{{ isAccommodation ? $t('widget.departure') : $t('widget.date_end') }}</span>
                                        <span class="rid-bk-val" :class="{'rid-muted': !selectedDates?.end}">{{ selectedDates?.end ? fmtDateShort(new Date(selectedDates.end)) : '—' }}</span>
                                    </button>
                                </div>

                                <!-- Inline calendar -->
                                <div v-if="calOpen" class="rid-bk-cal-pop">
                                    <div class="rid-cal-hd">
                                        <button class="rid-cal-nav-btn" @click="calShift(-1)" :aria-label="$t('common.back')">
                                            <svg class="rid-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 6-6 6 6 6"/></svg>
                                        </button>
                                        <b>{{ MONTHS[calView.m] }} {{ calView.y }}</b>
                                        <button class="rid-cal-nav-btn" @click="calShift(1)" :aria-label="$t('common.next')">
                                            <svg class="rid-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 6 6 6-6 6"/></svg>
                                        </button>
                                    </div>
                                    <div class="rw-cal-grid" @mouseleave="calHovered = null">
                                        <div v-for="d in DOW" :key="d" class="rw-cal-dow">{{ d }}</div>
                                        <button
                                            v-for="(cell, i) in calCells"
                                            :key="i"
                                            :class="calCellClass(cell)"
                                            @click="handleCalPick(cell)"
                                            @mouseenter="calSetHover(cell)"
                                        >{{ cell.d.getDate() }}</button>
                                    </div>
                                    <div v-if="nightsDiff > 0" class="rid-cal-nights">
                                        {{ isAccommodation ? transChoice('widget.nights', nightsDiff, {count: nightsDiff}) : transChoice('widget.days_count', nightsDiff, {count: nightsDiff}) }}
                                    </div>
                                </div>
                            </div>

                            <!-- Guests (accommodation only) -->
                            <div v-if="isAccommodation" class="rid-bk-block">
                                <button class="rid-bk-cell rid-bk-cell--full" :class="{'is-active': guestsOpen}" @click="guestsOpen = !guestsOpen; calOpen = false">
                                    <span class="rid-bk-lbl">{{ $t('widget.guests_label') }}</span>
                                    <span class="rid-bk-val">{{ transChoice('widget.guests_count', guests.adults + guests.children, { count: guests.adults + guests.children }) }}</span>
                                </button>
                                <div v-if="guestsOpen" class="rid-bk-cal-pop">
                                    <div class="rid-stepper-row">
                                        <div>
                                            <div class="rid-stepper-lbl">{{ $t('common.adults') }}</div>
                                            <div class="rid-stepper-sub">{{ $t('widget.adults_age_range') }}</div>
                                        </div>
                                        <div class="rw-counter">
                                            <button @click="guests.adults = Math.max(1, guests.adults - 1)" :disabled="guests.adults <= 1">−</button>
                                            <span>{{ guests.adults }}</span>
                                            <button @click="guests.adults++"
                                                :disabled="(item?.max_adults != null && guests.adults >= item.max_adults)
                                                    || (item?.max_pop != null && (guests.adults + guests.children) >= item.max_pop)">+</button>
                                        </div>
                                    </div>
                                    <div class="rid-stepper-row">
                                        <div>
                                            <div class="rid-stepper-lbl">{{ $t('common.children') }}</div>
                                            <div class="rid-stepper-sub">{{ $t('widget.children_age_range') }}</div>
                                        </div>
                                        <div class="rw-counter">
                                            <button @click="guests.children = Math.max(0, guests.children - 1)" :disabled="guests.children <= 0">−</button>
                                            <span>{{ guests.children }}</span>
                                            <button @click="guests.children++"
                                                :disabled="(item?.max_children != null && guests.children >= item.max_children)
                                                    || (item?.max_pop != null && (guests.adults + guests.children) >= item.max_pop)">+</button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Price breakdown -->
                            <div v-if="priceBreakdown && priceBreakdown.nights > 0" class="rid-bk-totals">
                                <div class="rid-bk-tot-row rid-muted">
                                    <span>{{ fmtKc(priceBreakdown.base) }} × {{ isAccommodation ? transChoice('widget.nights', priceBreakdown.nights, {count: priceBreakdown.nights}) : transChoice('widget.days_count', priceBreakdown.nights, {count: priceBreakdown.nights}) }}</span>
                                    <span>{{ fmtKc(priceBreakdown.subtotal) }}</span>
                                </div>
                                <div v-if="priceBreakdown.discountPct > 0" class="rid-bk-tot-row rid-muted" style="color: #16a34a">
                                    <span>{{ $t('widget.discount_pct', { pct: priceBreakdown.discountPct }) }}</span>
                                    <span>−{{ fmtKc(priceBreakdown.discountAmount) }}</span>
                                </div>
                                <div class="rid-bk-tot-row rid-bk-tot-total">
                                    <span>{{ $t('widget.bill_total') }}</span>
                                    <span>{{ fmtKc(priceBreakdown.total) }}</span>
                                </div>
                            </div>

                            <!-- CTA -->
                            <button class="rid-btn rid-btn-primary rid-cta" @click="openCheckout"
                                :disabled="!selectedDates?.start || !selectedDates?.end">
                                {{ (!selectedDates?.start || !selectedDates?.end) ? $t('widget.select_date_label') : $t('widget.book') }}
                                <svg v-if="selectedDates?.start && selectedDates?.end" class="rid-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                            </button>
                        </template>

                        <p class="rid-bk-hint">{{ $t('widget.no_upfront_payment') }}</p>
                    </div>
                </aside>
            </div>
        </div>

        <!-- Mobile sticky bar -->
        <div class="rid-mobile-bar">
            <div>
                <div class="rid-mobile-price">{{ fmtKc(itemBasePrice()) }}<small>/ {{ itemUnit() }}</small></div>
                <div v-if="priceBreakdown && priceBreakdown.nights > 0" class="rid-mobile-nights">{{ isAccommodation ? transChoice('widget.nights', priceBreakdown.nights, {count: priceBreakdown.nights}) : transChoice('widget.days_count', priceBreakdown.nights, {count: priceBreakdown.nights}) }}</div>
            </div>
            <button class="rid-btn rid-btn-primary" @click="openCheckout">
                {{ $t('widget.book') }}
            </button>
        </div>

        <!-- Checkout drawer -->
        <rental-checkout-drawer
            v-model="checkoutVisible"
            :item="item"
            :forms="forms"
            :initial-dates="selectedDates"
            :initial-guests="guests"
            :wizard-theme="wizardTheme"
        />

        <powered-by-roori v-if="forms.show_banner"/>
    </div>
</template>

<style scoped>
/* ─── Page shell ─────────────────────────────────────────────────────────── */
.rid-page {
    position: relative;
    z-index: 1;
    min-height: 100vh;
    background: var(--rw-bg, #F7F7F4);
    color: var(--rw-ink, #0F1115);
    font-family: var(--rw-font-body, ui-sans-serif, sans-serif);
    font-size: 15px; /* base — child sizes in em scale with a11y font control */
    padding-bottom: 80px;
}
.rid-page.is-transparent {
    background: transparent;
}

.rid-shell {
    max-width: 1320px;
    margin: 0 auto;
    padding: 0 24px;
}

/* ─── Header ─────────────────────────────────────────────────────────────── */
.rid-close-btn {
    display: flex; align-items: center; gap: 6px; padding: 8px 14px;
    border: 1px solid var(--rw-line); background: none; border-radius: var(--rw-r-md, 12px);
    font-family: inherit; font-size: .867em; font-weight: 500; color: var(--rw-ink-2);
    cursor: pointer; transition: background 0.12s, color 0.12s;
}
.rid-close-btn:hover { background: var(--rw-bg); color: var(--rw-ink); }

/* ─── Hero ────────────────────────────────────────────────────────────────── */
.rid-hero { padding: 32px 0 0; }
.rid-hero-head { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 20px; gap: 16px; }
.rid-eyebrow {
    display: inline-flex; align-items: center; gap: 8px; margin-bottom: 10px;
}
.rid-eyebrow span {
    display: inline-block; padding: 2px 8px; border-radius: 4px;
    background: var(--rw-brand-tint, #F1F5FC); color: var(--rw-brand-2, #1d5cbf);
    font-family: var(--rw-font-body); font-size: .667em; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.05em;
}
.rid-title { font-family: var(--rw-font-display); font-size: 2.4em; font-weight: 700; letter-spacing: -0.025em; margin: 0 0 8px; line-height: 1.1; }
.rid-sub { display: flex; flex-wrap: wrap; gap: 8px 16px; font-size: .867em; color: var(--rw-ink-2); }
.rid-sub span::before { content: '·'; margin-right: 8px; color: var(--rw-ink-4); }
.rid-sub span:first-child::before { display: none; }

/* ─── Gallery ─────────────────────────────────────────────────────────────── */
.rid-gallery {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    grid-template-rows: repeat(2, 200px);
    gap: 6px;
    border-radius: var(--rw-r-lg, 20px);
    overflow: hidden;
    margin-bottom: 0;
}
.rid-gallery-main { grid-column: 1; grid-row: 1 / 3; }
.rid-gallery-cell {
    position: relative; overflow: hidden; cursor: pointer;
    background: var(--rw-line-2);
}
.rid-gallery-cell img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.3s; }
.rid-gallery-cell:hover img { transform: scale(1.03); }


.rid-more-overlay {
    position: absolute; inset: 0; background: rgba(15,17,21,0.45);
    display: flex; align-items: center; justify-content: center;
    font-size: .933em; font-weight: 600; color: #fff; letter-spacing: 0.02em;
}

/* ─── Main layout ─────────────────────────────────────────────────────────── */
.rid-main {
    display: grid;
    grid-template-columns: 1fr 380px;
    gap: 48px;
    padding: 40px 0 60px;
    align-items: start;
}

/* ─── Info sections ───────────────────────────────────────────────────────── */
.rid-section { padding: 28px 0; border-bottom: 1px solid var(--rw-line-2); }
.rid-section:first-child { padding-top: 0; }
.rid-section:last-child { border-bottom: none; }
.rid-section-hd { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.rid-section-hd h3 { font-family: var(--rw-font-display); font-size: 1.333em; font-weight: 600; margin: 0; letter-spacing: -0.01em; }
.rid-bk-tag {
    font-family: var(--rw-font-mono); font-size: .667em; color: var(--rw-ink-3);
    letter-spacing: 0.06em; text-transform: uppercase;
    border: 1px solid var(--rw-line); border-radius: 20px; padding: 3px 10px;
}
.rid-desc { font-size: .933em; color: var(--rw-ink-2); line-height: 1.65; }
.rid-desc :deep(p) { margin: 0 0 10px; }
.rid-desc :deep(p:last-child) { margin-bottom: 0; }

.rid-amenities { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.rid-amenity {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 12px 14px; background: var(--rw-bg); border: 1px solid var(--rw-line);
    border-radius: var(--rw-r-md, 12px);
}
.rid-amenity-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--brand); flex-shrink: 0; margin-top: 4px; }
.rid-amenity b { display: block; font-size: .867em; font-weight: 600; color: var(--rw-ink); }
.rid-amenity span { display: block; font-size: .8em; color: var(--rw-ink-3); margin-top: 1px; }

.rid-rules { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0; }
.rid-rule { display: flex; flex-direction: column; gap: 3px; padding: 12px 0; border-bottom: 1px solid var(--rw-line-2); }
.rid-rule:nth-last-child(-n+2) { border-bottom: none; }
.rid-rule-lbl { font-size: .733em; color: var(--rw-ink-3); font-family: var(--rw-font-mono); text-transform: uppercase; letter-spacing: 0.04em; font-weight: 600; }
.rid-rule-val { font-size: .933em; font-weight: 500; color: var(--rw-ink); }

/* ─── Booking widget ─────────────────────────────────────────────────────── */
.rid-booking { position: sticky; top: 80px; }
.rid-bk-frame {
    background: var(--rw-paper, #fff);
    border: 1.5px solid var(--rw-line, #E5E2DC);
    border-radius: var(--rw-r-lg, 20px);
    padding: 24px;
    display: flex; flex-direction: column; gap: 16px;
}
.rid-bk-hd { display: flex; align-items: center; justify-content: space-between; }
.rid-bk-price { font-family: var(--rw-font-display); font-size: 1.6em; font-weight: 700; letter-spacing: -0.02em; }
.rid-bk-price small { font-family: var(--rw-font-body); font-size: .542em; font-weight: 400; color: var(--rw-ink-3); margin-left: 4px; }

.rid-bk-block {
    border: 1.5px solid var(--rw-line, #E5E2DC);
    border-radius: var(--rw-r-md, 12px);
    overflow: hidden;
}
.rid-bk-block-row { display: flex; }
.rid-bk-sep { width: 1px; background: var(--rw-line); flex-shrink: 0; }
.rid-bk-cell {
    flex: 1; display: flex; flex-direction: column; gap: 2px; padding: 12px 14px;
    background: none; border: none; cursor: pointer; text-align: left; font-family: inherit;
    transition: background 0.12s;
}
.rid-bk-cell:hover, .rid-bk-cell.is-active { background: var(--rw-bg); }
.rid-bk-cell--full { flex: none; width: 100%; }
.rid-bk-lbl { font-family: var(--rw-font-mono); font-size: .667em; color: var(--rw-ink-3); text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; }
.rid-bk-val { font-size: .933em; font-weight: 600; color: var(--rw-ink); }
.rid-muted { color: var(--rw-ink-4) !important; font-weight: 400 !important; }

.rid-bk-cal-pop { padding: 14px; border-top: 1px solid var(--rw-line-2); }
.rid-cal-hd { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.rid-cal-hd b { font-size: .867em; font-weight: 600; }
.rid-cal-nav-btn {
    width: 28px; height: 28px; border-radius: 999px; display: flex; align-items: center; justify-content: center;
    border: 1px solid var(--rw-line); color: var(--rw-ink-2); background: none; cursor: pointer; font-family: inherit;
}
.rid-cal-nav-btn:hover { border-color: var(--rw-ink-3); color: var(--rw-ink); }
.rid-cal-nights { font-size: .8em; color: var(--rw-ink-3); text-align: center; margin-top: 8px; }
.rid-cal-nights b { color: var(--rw-ink); font-weight: 600; }

/* ─── Calendar cells (scoped copy for booking widget inline cal) ─────────── */
.rw-cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
.rw-cal-dow { font-size: .667em; font-family: var(--rw-font-mono); color: var(--rw-ink-3); text-align: center; padding: 5px 0; font-weight: 500; letter-spacing: 0.04em; }
.rw-cal-cell {
    height: 34px; display: flex; align-items: center; justify-content: center; font-size: .8em;
    border-radius: var(--rw-r-sm); color: var(--rw-ink-2); position: relative;
    border: none; background: none; cursor: pointer; font-family: inherit; padding: 0;
}
.rw-cal-cell.out { color: var(--rw-ink-4); opacity: 0.4; pointer-events: none; }
.rw-cal-cell.disabled { color: var(--rw-ink-4); text-decoration: line-through; opacity: 0.5; pointer-events: none; }
.rw-cal-cell.blocked-start, .rw-cal-cell.blocked-end { color: var(--rw-ink-4); opacity: 0.4; cursor: not-allowed; background: repeating-linear-gradient(-45deg, transparent, transparent 3px, rgba(0,0,0,0.06) 3px, rgba(0,0,0,0.06) 6px); }
.rw-cal-cell:not(.out):not(.disabled):not(.blocked-start):not(.blocked-end):hover { background: var(--rw-bg); }
.rw-cal-cell.in-range { background: var(--rw-brand-tint); border-radius: 0; }
.rw-cal-cell.range-start { background: var(--brand); color: #fff; border-radius: var(--rw-r-sm) 0 0 var(--rw-r-sm); font-weight: 600; }
.rw-cal-cell.range-end { background: var(--brand); color: #fff; border-radius: 0 var(--rw-r-sm) var(--rw-r-sm) 0; font-weight: 600; }
.rw-cal-cell.range-single { background: var(--brand); color: #fff; border-radius: var(--rw-r-sm); font-weight: 600; }
.rw-cal-cell.today::after { content: ""; position: absolute; bottom: 4px; left: 50%; transform: translateX(-50%); width: 3px; height: 3px; border-radius: 999px; background: currentColor; }
.rw-cal-cell.busy-full::before { content: ""; position: absolute; bottom: 4px; left: 50%; transform: translateX(-50%); width: 4px; height: 4px; border-radius: 999px; background: #ef4444; }
.rw-cal-cell.busy-partial::before { content: ""; position: absolute; bottom: 4px; left: 50%; transform: translateX(-50%); width: 4px; height: 4px; border-radius: 999px; background: #f59e0b; }

/* ─── Booking totals ──────────────────────────────────────────────────────── */
.rid-bk-totals { display: flex; flex-direction: column; gap: 6px; border-top: 1px solid var(--rw-line-2); padding-top: 14px; }
.rid-bk-tot-row { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; font-size: .867em; color: var(--rw-ink-2); }
.rid-bk-tot-row.rid-muted { color: var(--rw-ink-3); font-size: .8em; }
.rid-bk-tot-total { margin-top: 4px; padding-top: 10px; border-top: 1px solid var(--rw-line-2); font-family: var(--rw-font-display); font-weight: 600; font-size: 1.067em; color: var(--rw-ink); }

/* ─── Buttons ─────────────────────────────────────────────────────────────── */
.rid-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    height: 48px; padding: 0 22px; border-radius: var(--rw-r-md);
    font-weight: 600; font-size: .933em; cursor: pointer; transition: background 0.12s, opacity 0.12s;
    font-family: inherit; border: none;
}
.rid-btn-primary { background: var(--brand); color: #fff; }
.rid-btn-primary:hover:not(:disabled) { background: var(--rw-brand-2, var(--brand)); filter: brightness(1.08); }
.rid-btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }
.rid-cta { width: 100%; }
.rid-icon { width: 16px; height: 16px; flex-shrink: 0; }

.rid-bk-single-hint { font-size: .867em; color: var(--rw-ink-3); margin: 0; }
.rid-bk-hint { font-size: .8em; color: var(--rw-ink-4); text-align: center; margin: 0; }

/* ─── Guest stepper ───────────────────────────────────────────────────────── */
.rid-stepper-row { display: flex; justify-content: space-between; align-items: center; }
.rid-stepper-lbl { font-size: .933em; font-weight: 500; color: var(--rw-ink); }
.rid-stepper-sub { font-size: .8em; color: var(--rw-ink-3); margin-top: 1px; }
.rw-counter { display: flex; align-items: center; gap: 12px; }
.rw-counter button {
    width: 30px; height: 30px; border-radius: 50%; border: 1.5px solid var(--rw-line); background: var(--rw-paper);
    font-size: 1.133em; line-height: 1; cursor: pointer; color: var(--rw-ink); display: flex; align-items: center; justify-content: center;
}
.rw-counter button:disabled { opacity: 0.3; cursor: default; }
.rw-counter span { font-size: 1em; font-weight: 600; min-width: 18px; text-align: center; color: var(--rw-ink); }

/* ─── Mobile sticky bar ───────────────────────────────────────────────────── */
.rid-mobile-bar {
    display: none;
    position: fixed; bottom: 0; left: 0; right: 0; z-index: 40;
    background: var(--rw-paper); border-top: 1px solid var(--rw-line-2);
    padding: 12px 20px; align-items: center; justify-content: space-between; gap: 16px;
    box-shadow: 0 -8px 30px -8px rgba(15,17,21,0.12);
}
.rid-mobile-price { font-family: var(--rw-font-display); font-size: 1.2em; font-weight: 700; letter-spacing: -0.015em; }
.rid-mobile-price small { font-size: .667em; font-weight: 400; color: var(--rw-ink-3); margin-left: 3px; }
.rid-mobile-nights { font-size: .8em; color: var(--rw-ink-3); margin-top: 1px; }

/* ─── Responsive ─────────────────────────────────────────────────────────── */
@media (max-width: 1024px) {
    .rid-main { grid-template-columns: 1fr; gap: 0; }
    .rid-booking { position: static; display: none; }
    .rid-mobile-bar { display: flex; }
    .rid-gallery { grid-template-rows: repeat(2, 160px); }
}

@media (max-width: 768px) {
    .rid-shell { padding: 0 16px; }
    .rid-title { font-size: 1.733em; }
    .rid-gallery { grid-template-columns: 1fr 1fr; grid-template-rows: 200px 120px; }
    .rid-gallery-main { grid-column: 1 / 3; grid-row: 1; }
    .rid-gallery-cell:nth-child(3), .rid-gallery-cell:nth-child(4) { display: none; }
    .rid-amenities { grid-template-columns: 1fr; }
    .rid-rules { grid-template-columns: 1fr; }
}

@media (max-width: 480px) {
    .rid-gallery { grid-template-rows: 180px 100px; }
    .rid-title { font-size: 1.467em; }
}
</style>
